import { createFileRoute } from '@tanstack/react-router';
import {
  getClasses,
  getDiscoveryFiltersQueryFn,
  getInstructors,
  getStudios,
} from '@/lib/api/v2/discovery';
import { authenticatedGetServer } from '@/lib/server-fns/authenticated-get-fns';
// Key-builders shared with the discovery hooks so SSR prefetch keys can never
// drift from the client query keys (the #1 hydration-break risk).
import {
  discoveryClassesKey,
  discoveryFiltersKey,
  discoveryInstructorsKey,
  discoveryStudiosKey,
} from '@/hooks/data/useDiscovery';
import { toSGTCivilKey } from '@/lib/utils/date';
import { cacheFirst } from '@/lib/queries/cache-first';
import { createDeferredCatalogueLoaderData } from './-catalogue-loader-data';

type DiscoverySearch = {
  tab: 'classes' | 'instructors' | 'studios';
  favourites?: 'true';
  studio?: string;
};

const validateDiscoverySearch = (search: Record<string, unknown>): DiscoverySearch => {
  const favourites =
    search.favourites === 'true' ||
    search.favourites === true ||
    search.favourites === '1' ||
    search.favourites === 1;

  const tab = search.tab === 'instructors' || search.tab === 'studios' ? search.tab : 'classes';
  return {
    tab,
    favourites: favourites ? 'true' : undefined,
    studio: (search.studio as string) || undefined,
  };
};

export const Route = createFileRoute('/_authenticated/discovery')({
  validateSearch: validateDiscoverySearch,
  // Search drives the visible tab client-side. Keeping it out of loader deps lets
  // tab changes retain the mounted discovery shell while the active Query list
  // resolves, instead of replacing the whole route with router pending UI.
  loaderDeps: () => ({}),
  headers: () =>
    // /discovery renders per-user data whenever favourites=true (and always
    // embeds the authenticated session), so the document must never be served
    // from a shared cache (#868). ensureSsrNoStore forces the same on every SSR
    // HTML document; the explicit route header is defense-in-depth. Unconditional:
    // a per-search header fn breaks useLoaderData typing on this TanStack version.
    ({ 'Cache-Control': 'private, no-store' }),
  loader: async ({ context, location }) => {
    const search = validateDiscoverySearch(location.search as Record<string, unknown>);
    // Today in SGT (UTC+8) as 'YYYY-MM-DD' — the selected-day key shared with the
    // client, which seeds its selected date from this key so hydration can never
    // drift across the SGT midnight boundary (see #874).
    const dateStr = toSGTCivilKey(new Date());

    const showFavourites = search.favourites === 'true';
    const studioFilters = search.studio ? [`studio-${search.studio}`] : [];
    const studioParams = search.studio ? [search.studio] : [];
    const qc = context.queryClient;
    const initialFiltersPromise = qc.ensureQueryData({
      queryKey: discoveryFiltersKey(),
      queryFn: getDiscoveryFiltersQueryFn,
    });
    // Favourites-filtered catalogue is per-user. During SSR the plain apiClient
    // has no cookie jar, so getClasses/getInstructors/getStudios would prefetch
    // the ANONYMOUS (empty) favourites result and hydrate it over the learner's
    // real favourites: route that first page through the cookie-forwarding
    // server fn so SSR returns the signed-in learner's favourites; anonymous
    // sessions keep the backend's empty-favourites contract (#868). On
    // client-side navigations that rationale is void — the direct catalogue
    // clients already attach the learner's credentials when favourites=true —
    // so the 2-hop server-fn proxy would be pure latency there.
    const favouritesFetch =
      showFavourites && typeof window === 'undefined'
        ? (path: string) => authenticatedGetServer({ data: path })
        : null;

    // One first-page fetcher per tab. The favourites path mirrors the
    // querystring the hooks' direct calls build (search/filters are empty on
    // the initial page); the direct path forwards favourites so browser
    // navigations still hit the signed-in learner's list.
    const listQuery: {
      key: readonly unknown[];
      fetchFirstPage: (pageParam: string | undefined) => Promise<unknown>;
    } =
      search.tab === 'instructors'
        ? {
            key: discoveryInstructorsKey('', showFavourites, studioFilters),
            fetchFirstPage: (pageParam) => {
              if (favouritesFetch) {
                const params = new URLSearchParams({ favourites: 'true' });
                studioParams.forEach((s) => params.append('studios', s));
                if (pageParam) params.append('cursor', pageParam);
                return favouritesFetch(`/v2/instructors?${params.toString()}`);
              }
              return getInstructors({
                cursor: pageParam,
                search: undefined,
                studios: studioParams,
                genres: [],
                favourites: showFavourites,
              });
            },
          }
        : search.tab === 'studios'
          ? {
              key: discoveryStudiosKey('', showFavourites, []),
              fetchFirstPage: (pageParam) => {
                if (favouritesFetch) {
                  const params = new URLSearchParams({ favourites: 'true' });
                  if (pageParam) params.append('cursor', pageParam);
                  return favouritesFetch(`/v2/studios?${params.toString()}`);
                }
                return getStudios({
                  cursor: pageParam,
                  search: undefined,
                  genres: [],
                  favourites: showFavourites,
                });
              },
            }
          : {
              key: discoveryClassesKey(dateStr, dateStr, studioFilters, showFavourites, ''),
              fetchFirstPage: (pageParam) => {
                if (favouritesFetch) {
                  const params = new URLSearchParams({
                    startDate: dateStr,
                    endDate: dateStr,
                    favourites: 'true',
                  });
                  studioParams.forEach((s) => params.append('studios', s));
                  if (pageParam) params.append('cursor', pageParam);
                  return favouritesFetch(`/v2/classes?${params.toString()}`);
                }
                return getClasses(dateStr, dateStr, {
                  levels: [],
                  genres: [],
                  studios: studioParams,
                  cursor: pageParam,
                  search: undefined,
                  favourites: showFavourites,
                });
              },
            };

    // Cache-first: a cached list streams instantly while the plane heals
    // staleness in the background, so re-entry after the 5-minute staleTime
    // never re-suspends the whole results region; a cold cache keeps today's
    // streamed loader fetch, and its rejection still lands in the boundary's
    // retry UI. A background-refresh failure surfaces through the mounted
    // query's own list-local error instead.
    const { cached, stream } = cacheFirst(qc, listQuery.key, () =>
      qc.ensureInfiniteQueryData({
        queryKey: listQuery.key,
        queryFn: ({ pageParam }) => listQuery.fetchFirstPage(pageParam),
        initialPageParam: undefined as string | undefined,
      })
    );
    const initialListPromise = cached !== undefined ? Promise.resolve(cached) : stream();

    return {
      ...createDeferredCatalogueLoaderData(initialListPromise),
      initialFiltersPromise,
      initialTab: search.tab,
      initialSGTDayKey: dateStr,
    };
  },
});
